/**
 * MetricsPeriodSwitch — the 30 j / 90 j / 12 mois bascule that windows the trend lines + contextualises the
 * KPI deltas on the Overview cockpit. A WAI-ARIA radiogroup with full roving-keyboard support (Arrow/Home/
 * End), token-styled as a segmented control — mirrors the analytics PeriodSwitch verbatim (kept local so the
 * management surface owns its own period type + i18n keys). Keyboard-first: the active segment is the single
 * tab stop; arrows move + select. Mirrors under dir="rtl".
 */
import { useRef, type KeyboardEvent } from "react";
import { useT } from "@/app";
import { METRICS_PERIODS, type MetricsPeriod } from "../metrics.types";

const LABEL_KEY: Record<MetricsPeriod, string> = {
  "30d": "mgmt.overview.period.30d",
  "90d": "mgmt.overview.period.90d",
  "12mo": "mgmt.overview.period.12mo",
};

export function MetricsPeriodSwitch({
  value,
  onChange,
}: {
  value: MetricsPeriod;
  onChange: (p: MetricsPeriod) => void;
}) {
  const { t } = useT();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (from: number, dir: number) => {
    const next = (from + dir + METRICS_PERIODS.length) % METRICS_PERIODS.length;
    onChange(METRICS_PERIODS[next]!);
    refs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        move(idx, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        move(idx, -1);
        break;
      case "Home":
        e.preventDefault();
        onChange(METRICS_PERIODS[0]!);
        refs.current[0]?.focus();
        break;
      case "End":
        e.preventDefault();
        onChange(METRICS_PERIODS[METRICS_PERIODS.length - 1]!);
        refs.current[METRICS_PERIODS.length - 1]?.focus();
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={t("mgmt.overview.period.label")}
      style={{
        display: "inline-flex",
        gap: "var(--nv-s-1)",
        padding: "var(--nv-s-1)",
        borderRadius: "var(--nv-r-pill)",
        background: "var(--nv-surface-2)",
        border: "1px solid var(--nv-line-faint)",
      }}
    >
      {METRICS_PERIODS.map((p, i) => {
        const selected = p === value;
        return (
          <button
            key={p}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(p)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className="nv-focusable"
            style={{
              appearance: "none",
              cursor: "pointer",
              border: "none",
              minHeight: "var(--nv-min-touch)",
              padding: "0 var(--nv-s-3)",
              borderRadius: "var(--nv-r-pill)",
              fontSize: "var(--nv-fs-label)",
              fontWeight: selected ? 600 : 500,
              color: selected ? "var(--nv-white)" : "var(--nv-slate)",
              background: selected ? "var(--nv-azure)" : "transparent",
              transition: "background var(--nv-fast) var(--nv-ease)",
            }}
          >
            {t(LABEL_KEY[p])}
          </button>
        );
      })}
    </div>
  );
}
